import { createSelector, createSelectorCreator, lruMemoize } from 'reselect';
import { isEqual, get } from 'lodash-es';
import type { PropertyPath } from 'lodash-es';
import { parseDate } from 'utils';

// reselect v5 renamed `defaultMemoize` -> `lruMemoize`; behavior is unchanged.
// `isEqual` is passed through as lruMemoize's equality check (deep-equal caching).
export const createDeepEqualSelector = createSelectorCreator(lruMemoize, isEqual);

// Returns a referentially-stable slice: the extraction happens in the result
// function, and `resultEqualityCheck` hands back the previously-cached
// reference whenever the freshly-extracted slice is deep-equal to it.
export const baseSelect = (path: PropertyPath, defaultValue?: unknown) =>
  createSelector(
    // `s` is arbitrary Redux state; lodash `get` walks the path dynamically.
    [(s: unknown) => s],
    (s: unknown) => get(s, path, defaultValue),
    { memoize: lruMemoize, memoizeOptions: { resultEqualityCheck: isEqual } },
  );

export const sortCustomFields = <T extends { orderIndex: number }>(
  fields: Record<string, T>,
): T[] => Object.values(fields).sort((a, b) => a.orderIndex - b.orderIndex);

interface LastUsedRecord {
  uid: string;
  product_access_id: string;
  updated_at: string;
}

export const filterUserLastUsedDate = (
  userId: string,
  licenseId: string,
  lastUsedRecords: LastUsedRecord[],
): Date | string | null => {
  /* eslint-disable camelcase */
  const lastUsedRecordsForUser = lastUsedRecords.filter(
    ({ uid, product_access_id }) => licenseId === product_access_id && uid === userId,
  );
  const sortedLastUsedRecords =
    lastUsedRecordsForUser.length >= 1
      ? lastUsedRecordsForUser.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1))
      : [];
  return sortedLastUsedRecords.length >= 1 ? parseDate(sortedLastUsedRecords[0].updated_at) : '';
};
